import { useQueryClient } from "@tanstack/react-query";

import { useReviewCaseMutation } from "src/api/queries";
import { AssigneeSelect } from "src/base-components/AssigneeSelect";
import { OrganizationUser } from "src/clients/taktile-api";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

type UserPickerProps = {
  value?: Nullable<string>;
  users: OrganizationUser[];
  workspaceId: string;
  onChange: (value: Nullable<string>) => void;
  isInRow?: boolean;
};

export const UserPicker: React.FC<UserPickerProps> = ({
  value,
  users,
  workspaceId,
  onChange,
  isInRow,
}) => (
  <AssigneeSelect
    dataLoc="manual-review-user-picker"
    isInRow={isInRow}
    isLoading={users.length === 0}
    users={users}
    value={value}
    variant="inline"
    workspaceId={workspaceId}
    onChange={onChange}
  />
);

export const UserPickerCell: React.FC<
  Pick<UserPickerProps, "value" | "users" | "isInRow"> & {
    caseId: string;
    etag: string;
    flowSlug: string;
  }
> = ({ value, users, caseId, etag, flowSlug, isInRow }) => {
  const queryClient = useQueryClient();

  const { workspace } = useWorkspaceContext();
  const caseMutation = useReviewCaseMutation(
    workspace.base_url,
    flowSlug,
    caseId,
  );

  const updateAssignee = async (userId: Nullable<string>) => {
    try {
      await caseMutation.mutateAsync({ assignee: userId, etag });
    } catch (e: unknown) {
      if (isPreconditionError(e)) {
        await queryClient.invalidateQueries({
          queryKey: ["reviewCases", workspace.base_url, flowSlug],
        });
        toastActions.failure({
          title: "Failed to update assignee",
          description: "We refreshed review cases. Please try again",
        });
      } else {
        toastActions.failure({
          title: "Failed to update assignee",
          description: TAKTILE_TEAM_NOTIFIED,
        });
      }
      logger.error(e);
    }
  };

  return (
    <div
      className="overflow-hidden truncate pr-2"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <UserPicker
        isInRow={isInRow}
        users={users}
        value={value}
        workspaceId={workspace.id}
        onChange={updateAssignee}
      />
    </div>
  );
};
